<template>
  <div class="ncrm-mitp-sync-panel">
    <v-alert type="info" class="mb-4">
      <div class="text-subtitle-2">{{ $t("ncrm.integrations.appSyncs.mitp.title") }}</div>
      <div class="text-body-2">
        {{ $t("ncrm.integrations.appSyncs.mitp.description") }}
      </div>
    </v-alert>
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4 app-toolbar-surface">
      <div class="d-flex flex-wrap align-center ga-3">
        <v-select
          v-model="jobStatusFilter"
          :items="jobStatusOptions"
          :label="$t('ncrm.integrations.appSyncs.jobs.filters.status')"
          hide-details
          clearable
          class="ncrm-mitp-sync-panel__filter"
        />
      </div>
      <div class="d-flex flex-wrap align-center ga-2">
        <v-btn variant="text" color="primary" :disabled="jobsLoading" @click="refreshAll">
          <v-icon start>mdi-refresh</v-icon>
          {{ $t("app.actions.refresh") }}
        </v-btn>
        <v-btn color="primary" :loading="runLoading" @click="handleRunSync">
          <v-icon start>mdi-play</v-icon>
          {{ $t("ncrm.integrations.appSyncs.mitp.run") }}
        </v-btn>
      </div>
    </div>

    <v-card v-if="selectedJob" variant="outlined" class="mb-4">
      <v-card-text class="pa-4">
        <div class="d-flex flex-wrap align-center justify-space-between ga-3">
          <div>
            <div class="text-subtitle-1 ncrm-text-strong">
              {{ $t("ncrm.integrations.appSyncs.currentJob.title", { id: selectedJob.id }) }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.integrations.appSyncs.currentJob.subtitle") }}
            </div>
          </div>
          <v-chip
            size="small"
            :color="resolveStatusMeta(selectedJob.status).color"
            :variant="resolveStatusMeta(selectedJob.status).variant"
          >
            {{ translateJobStatus(selectedJob.status) }}
          </v-chip>
        </div>

        <div class="mt-4">
          <v-progress-linear
            :model-value="jobProgressPercent"
            :indeterminate="selectedJobIsActive && jobProgressPercent === 0"
            color="primary"
            rounded
          />
          <div class="d-flex flex-wrap justify-space-between text-caption text-medium-emphasis mt-2 ga-2">
            <span>
              {{
                $t("ncrm.integrations.appSyncs.currentJob.progress", {
                  processed: selectedJob.processed_count || 0,
                  total: selectedJob.registry_total || 0,
                })
              }}
            </span>
            <span>{{ jobProgressPercent }}%</span>
          </div>
        </div>

        <div class="d-flex flex-wrap ga-2 mt-4">
          <v-chip color="success">
            {{ $t("ncrm.integrations.appSyncs.counters.updated", { count: selectedJob.updated_count || 0 }) }}
          </v-chip>
          <v-chip color="warning">
            {{
              $t("ncrm.integrations.appSyncs.counters.skippedExisting", {
                count: selectedJob.skipped_existing_count || 0,
              })
            }}
          </v-chip>
          <v-chip color="neutral">
            {{ $t("ncrm.integrations.appSyncs.counters.unchanged", { count: selectedJob.unchanged_count || 0 }) }}
          </v-chip>
          <v-chip color="info">
            {{ $t("ncrm.integrations.appSyncs.counters.unmatched", { count: selectedJob.unmatched_count || 0 }) }}
          </v-chip>
          <v-chip color="error">
            {{ $t("ncrm.integrations.appSyncs.counters.error", { count: selectedJob.error_count || 0 }) }}
          </v-chip>
        </div>

        <div v-if="selectedJob.failure_reason" class="text-body-2 text-error mt-3">
          {{ selectedJob.failure_reason }}
        </div>
      </v-card-text>
    </v-card>
    <v-card variant="outlined">
      <v-card-text class="pa-0">
        <v-data-table-server
          v-model:items-per-page="jobsPerPage"
          v-model:page="jobsPage"
          :headers="jobHeaders"
          :items="jobs"
          :items-length="jobsTotal"
          :loading="jobsLoading"
          item-key="id"
          class="elevation-0 ncrm-sync-panel__table"
          @click:row="handleJobRowClick"
        >
          <template #item.status="{ item }">
            <v-chip
              size="small"
              :color="resolveStatusMeta(item.status).color"
              :variant="resolveStatusMeta(item.status).variant"
            >
              {{ translateJobStatus(item.status) }}
            </v-chip>
          </template>
          <template #item.progress="{ item }">
            {{
              $t("ncrm.integrations.appSyncs.currentJob.progress", {
                processed: item.processed_count || 0,
                total: item.registry_total || 0,
              })
            }}
          </template>
          <template #item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #item.last_heartbeat_at="{ item }">
            {{ formatDateTime(item.last_heartbeat_at) }}
          </template>
          <template #item.requested_by_user_name="{ item }">
            {{ item.requested_by_user_name || "-" }}
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.integrations.appSyncs.jobs.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <div class="d-flex flex-wrap align-center ga-3 my-4 app-toolbar-surface app-toolbar-surface--compact">
      <div class="text-subtitle-2 ncrm-text-strong">
        {{ $t("ncrm.integrations.appSyncs.items.title") }}
      </div>
      <v-spacer />
      <v-text-field
        v-model="itemSearch"
        :label="$t('ncrm.integrations.appSyncs.items.filters.search')"
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="ncrm-mitp-sync-panel__search"
      />
      <v-select
        v-model="itemStatusFilter"
        :items="itemStatusOptions"
        :label="$t('ncrm.integrations.appSyncs.items.filters.status')"
        hide-details
        clearable
        class="ncrm-mitp-sync-panel__filter"
      />
    </div>

    <v-card variant="outlined">
      <v-card-text class="pa-0">
        <v-data-table-server
          v-model:items-per-page="itemsPerPage"
          v-model:page="itemsPage"
          :headers="itemHeaders"
          :items="items"
          :items-length="itemsTotal"
          :loading="itemsLoading"
          item-key="id"
          class="elevation-0 ncrm-sync-panel__table"
        >
          <template #item.item_status="{ item }">
            <v-chip
              size="small"
              :color="resolveResultStatusMeta(item.item_status).color"
              :variant="resolveResultStatusMeta(item.item_status).variant"
            >
              {{ translateResultStatus(item.item_status) }}
            </v-chip>
          </template>
          <template #item.changed_fields="{ item }">
            {{ formatFieldList(item.changed_fields) }}
          </template>
          <template #item.skipped_fields="{ item }">
            {{ formatFieldList(item.skipped_fields) }}
          </template>
          <template #item.processed_at="{ item }">
            {{ formatDateTime(item.processed_at) }}
          </template>
          <template #item.detail_url="{ item }">
            <div class="d-flex flex-column align-start">
              <v-btn
                v-if="item.detail_url"
                :href="item.detail_url"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="text"
                color="primary"
                class="app-entity-link"
              >
                <v-icon start size="16">mdi-open-in-new</v-icon>
                {{ $t("ncrm.integrations.appSyncs.items.openSource") }}
              </v-btn>
              <v-btn
                v-if="item.ncrm_company_id"
                size="small"
                variant="text"
                color="primary"
                class="app-entity-link"
                @click="openMatchedCompany(item)"
              >
                <v-icon start size="16">mdi-domain</v-icon>
                {{ item.ncrm_company_name || $t("ncrm.integrations.appSyncs.items.openMatchedCompany") }}
              </v-btn>
              <v-btn
                v-else-if="canResolveItem(item)"
                size="small"
                variant="tonal"
                color="secondary"
                class="app-inline-action app-inline-action--secondary"
                @click="openResolveDialog(item)"
              >
                <v-icon start size="16">mdi-link-variant</v-icon>
                {{ $t("ncrm.integrations.appSyncs.items.resolve") }}
              </v-btn>
              <span v-else-if="!item.detail_url">-</span>
            </div>
          </template>
          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.integrations.appSyncs.items.empty") }}
            </v-alert>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <NcrmMitpResolveUnmatchedDialog
      v-model="resolveDialogOpen"
      :job-id="selectedJobId"
      :item="resolveItem"
      @error="showSnackbar($event, 'error')"
      @resolved="handleResolveCompleted"
    />
    <v-snackbar v-model="snackbar.show" :timeout="3000" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import NcrmMitpResolveUnmatchedDialog from "@/components/ncrm/integrations/NcrmMitpResolveUnmatchedDialog.vue";
import {
  formatMitpStatusLabel,
  isMitpJobActive,
  MITP_RESULT_STATUSES,
  resolveMitpProgressPercent,
  resolveMitpResultStatusMeta,
  resolveMitpStatusMeta,
} from "@/Utile/ncrmMitpSync.mjs";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "NcrmMitpSyncPanel",
  components: { NcrmMitpResolveUnmatchedDialog },
  data() {
    return {
      jobsLoading: false,
      jobs: [],
      jobsPage: 1,
      jobsPerPage: 10,
      jobsTotal: 0,
      jobStatusFilter: null,
      selectedJobId: null,
      selectedJob: null,
      itemsLoading: false,
      items: [],
      itemsPage: 1,
      itemsPerPage: 25,
      itemsTotal: 0,
      itemStatusFilter: null,
      itemSearch: "",
      resolveDialogOpen: false,
      resolveItem: null,
      runLoading: false,
      pollTimerId: null,
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    jobHeaders() {
      return [
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.createdAt"), key: "created_at" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.status"), key: "status" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.progress"), key: "progress" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.updated"), key: "updated_count" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.unmatched"), key: "unmatched_count" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.errors"), key: "error_count" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.requestedBy"), key: "requested_by_user_name" },
        { title: this.$t("ncrm.integrations.appSyncs.jobs.headers.lastHeartbeat"), key: "last_heartbeat_at" },
      ];
    },
    itemHeaders() {
      return [
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.company"), key: "mitp_company_name" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.idno"), key: "mitp_idno" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.status"), key: "item_status" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.changedFields"), key: "changed_fields" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.skippedFields"), key: "skipped_fields" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.processedAt"), key: "processed_at" },
        { title: this.$t("ncrm.integrations.appSyncs.items.headers.source"), key: "detail_url" },
      ];
    },
    jobStatusOptions() {
      return ["queued", "running", "completed", "failed"].map((value) => ({
        title: this.translateJobStatus(value),
        value,
      }));
    },
    itemStatusOptions() {
      return MITP_RESULT_STATUSES.map((value) => ({
        title: this.translateResultStatus(value),
        value,
      }));
    },
    selectedJobIsActive() {
      return isMitpJobActive(this.selectedJob?.status);
    },
    jobProgressPercent() {
      return resolveMitpProgressPercent(this.selectedJob);
    },
  },
  watch: {
    jobsPage() {
      this.fetchJobs();
    },
    jobsPerPage() {
      this.jobsPage = 1;
      this.fetchJobs();
    },
    jobStatusFilter() {
      this.jobsPage = 1;
      this.fetchJobs();
    },
    selectedJobId() {
      this.itemsPage = 1;
      this.fetchSelectedJob();
      this.fetchItems();
    },
    itemsPage() {
      this.fetchItems();
    },
    itemsPerPage() {
      this.itemsPage = 1;
      this.fetchItems();
    },
    itemStatusFilter() {
      this.itemsPage = 1;
      this.fetchItems();
    },
    itemSearch() {
      this.itemsPage = 1;
      this.fetchItems();
    },
    selectedJobIsActive(value) {
      if (value) {
        this.startPolling();
        return;
      }
      this.stopPolling();
    },
  },
  mounted() {
    this.fetchJobs();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    resolveStatusMeta(status) {
      return resolveMitpStatusMeta(status);
    },
    resolveResultStatusMeta(status) {
      return resolveMitpResultStatusMeta(status);
    },
    translateJobStatus(status) {
      const normalized = String(status || "").trim().toLowerCase();
      const translated = this.$t(`ncrm.integrations.appSyncs.status.${normalized}`);
      return translated === `ncrm.integrations.appSyncs.status.${normalized}`
        ? formatMitpStatusLabel(status)
        : translated;
    },
    translateResultStatus(status) {
      const normalized = String(status || "").trim().toLowerCase();
      const translated = this.$t(`ncrm.integrations.appSyncs.resultStatus.${normalized}`);
      return translated === `ncrm.integrations.appSyncs.resultStatus.${normalized}`
        ? formatMitpStatusLabel(status)
        : translated;
    },
    formatDateTime(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return formatDisplayDateTime(date);
    },
    formatFieldList(fields) {
      const normalized = Array.isArray(fields) ? fields.filter(Boolean) : [];
      return normalized.length ? normalized.join(", ") : "-";
    },
    canResolveItem(item) {
      const status = String(item?.item_status || "").trim().toLowerCase();
      return status === "unmatched" || status === "error";
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    openMatchedCompany(item) {
      const companyId = Number(item?.ncrm_company_id || 0);
      if (companyId) this.$router.push(`/ncrm/companies/${companyId}`);
    },
    openResolveDialog(item) {
      this.resolveItem = item || null; this.resolveDialogOpen = true;
    },
    async handleResolveCompleted() {
      await this.fetchSelectedJob(); await this.fetchItems();
      this.showSnackbar(this.$t("ncrm.integrations.appSyncs.resolve.success"), "success");
    },
    async refreshAll() {
      await this.fetchJobs();
      await this.fetchSelectedJob();
      await this.fetchItems();
    },
    async handleRunSync() {
      this.runLoading = true;
      try {
        const { data } = await axios.post("/ncrm/integrations/mitp/jobs");
        const job = data?.job || null;
        if (job?.id) {
          this.selectedJobId = Number(job.id);
        }
        await this.fetchJobs();
        await this.fetchSelectedJob();
        await this.fetchItems();
        this.showSnackbar(this.$t("ncrm.integrations.appSyncs.mitp.started"), "success");
      } catch (error) {
        const job = error?.response?.data?.job || null;
        if (job?.id) {
          this.selectedJobId = Number(job.id);
        }
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.messages.runError"),
          "error"
        );
        await this.fetchJobs();
        await this.fetchSelectedJob();
      } finally {
        this.runLoading = false;
      }
    },
    pickPreferredJobId(items = []) {
      if (this.selectedJobId) return this.selectedJobId;
      const activeJob = items.find((entry) => isMitpJobActive(entry?.status));
      if (activeJob?.id) return Number(activeJob.id);
      if (items[0]?.id) return Number(items[0].id);
      return null;
    },
    async fetchJobs() {
      this.jobsLoading = true;
      try {
        const { data } = await axios.get("/ncrm/integrations/mitp/jobs", {
          params: {
            page: this.jobsPage,
            perPage: this.jobsPerPage,
            status: this.jobStatusFilter || undefined,
          },
        });
        this.jobs = Array.isArray(data?.items) ? data.items : [];
        this.jobsTotal = Number(data?.total || this.jobs.length || 0);
        const nextJobId = this.pickPreferredJobId(this.jobs);
        if (nextJobId !== this.selectedJobId) {
          this.selectedJobId = nextJobId;
        }
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.messages.loadJobsError"),
          "error"
        );
      } finally {
        this.jobsLoading = false;
      }
    },
    async fetchSelectedJob() {
      if (!this.selectedJobId) {
        this.selectedJob = null;
        this.stopPolling();
        return;
      }
      try {
        const { data } = await axios.get(`/ncrm/integrations/mitp/jobs/${this.selectedJobId}`);
        this.selectedJob = data || null;
      } catch (error) {
        this.selectedJob = null;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.messages.loadJobError"),
          "error"
        );
      }
    },
    async fetchItems() {
      if (!this.selectedJobId) {
        this.items = [];
        this.itemsTotal = 0;
        return;
      }
      this.itemsLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/integrations/mitp/jobs/${this.selectedJobId}/items`, {
          params: {
            page: this.itemsPage,
            perPage: this.itemsPerPage,
            status: this.itemStatusFilter || undefined,
            search: this.itemSearch || undefined,
          },
        });
        this.items = Array.isArray(data?.items) ? data.items : [];
        this.itemsTotal = Number(data?.total || this.items.length || 0);
      } catch (error) {
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.messages.loadItemsError"),
          "error"
        );
      } finally {
        this.itemsLoading = false;
      }
    },
    handleJobRowClick(_, row) {
      const item = row?.item || null;
      const jobId = Number(item?.id || 0);
      if (!jobId) return;
      this.selectedJobId = jobId;
    },
    startPolling() {
      if (this.pollTimerId || typeof window === "undefined") return;
      this.pollTimerId = window.setInterval(async () => {
        await this.fetchJobs();
        await this.fetchSelectedJob();
        await this.fetchItems();
      }, 5000);
    },
    stopPolling() {
      if (!this.pollTimerId || typeof window === "undefined") return;
      window.clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    },
  },
};
</script>

<style scoped>
.ncrm-mitp-sync-panel__filter {
  flex: 0 0 260px;
  min-width: 260px;
  width: 260px;
}
.ncrm-mitp-sync-panel__search {
  width: min(100%, 320px);
}

@media (max-width: 600px) {
  .ncrm-mitp-sync-panel__filter,
  .ncrm-mitp-sync-panel__search {
    flex: 1 1 100%;
    min-width: 0;
    width: 100%;
  }
}
</style>
